<script lang="ts">
  /**
   * Slim top status bar — hamburger button (toggles the left flyout
   * Drawer), API status badge, and a few inline controls (projection,
   * 2D/3D, edges) for one-click access without opening the drawer.
   *
   * Replaces the previous heavy Toolbar.svelte; the full controls panel
   * now lives in Drawer.svelte.
   */

  import { onMount } from 'svelte'
  import { drawer, filters } from './selection.svelte'
  import { apiStatus, checkHealth } from './api.svelte'
  import { appView } from './taskGraphStore.svelte'
  import MemorySwitch from './MemorySwitch.svelte'

  onMount(() => {
    void checkHealth()
    const i = setInterval(() => { void checkHealth() }, 30_000)
    return () => clearInterval(i)
  })

  function statusLabel(): string {
    if (apiStatus.retrying > 0) return `retrying (${apiStatus.retrying})`
    if (apiStatus.online) {
      const n = apiStatus.memoriesWithEmbeddings
      return n != null ? `${n.toLocaleString()} mem` : 'live'
    }
    return apiStatus.lastChecked == null ? 'checking…' : 'offline'
  }
</script>

<header class="sb">
  <button
    class="sb-hamburger"
    type="button"
    aria-label="Toggle controls drawer"
    aria-expanded={drawer.open}
    onclick={() => drawer.open = !drawer.open}
    title="Controls / Memories / Settings"
  >
    <span class="sb-hamburger-glyph">≡</span>
  </button>

  <span class="sb-tag">// nexus-mu</span>

  <span class="sb-viewtoggle">
    <button type="button" class:on={appView.mode === 'constellation'} onclick={() => appView.mode = 'constellation'} title="Memory constellation">MEM</button>
    <button type="button" class:on={appView.mode === 'tasks'} onclick={() => appView.mode = 'tasks'} title="Canonical task graph">TASKS</button>
  </span>

  <MemorySwitch />

  <span
    class="sb-status"
    class:online={apiStatus.online}
    class:retrying={apiStatus.retrying > 0}
    title={apiStatus.reason ?? 'Click to view connection details in drawer'}
  >
    <span class="sb-status-dot"></span>
    <span class="sb-status-text">{statusLabel()}</span>
  </span>

  <div class="sb-spacer"></div>

  {#if appView.mode === 'constellation'}
  <!-- Quick-access inline controls — full versions in drawer.
       Beta: only PCA is offered. UMAP/t-SNE/force are live-compute
       (22s–158s, exceeds the CF origin timeout) — re-enabled once the
       streaming-projection endpoint ships (mu-platform track). -->
  <select class="sb-sel" bind:value={filters.projection} title="Projection algorithm">
    <option value="pca">PCA</option>
  </select>

  <button
    class="sb-btn"
    class:on={filters.viewMode === '3d'}
    type="button"
    onclick={() => filters.viewMode = filters.viewMode === '2d' ? '3d' : '2d'}
    title="Toggle 2D/3D camera (D)"
  >{filters.viewMode === '2d' ? '2D' : '3D'}</button>

  <select class="sb-sel sb-sel-narrow" bind:value={filters.edgeMode} title="Edge mode (E to cycle)">
    <option value="none">— EDGES</option>
    <option value="selected">◆ SEL</option>
    <option value="elite">★ ELITE</option>
    <option value="apex">⚡ APEX</option>
    <option value="all">▦ ALL</option>
  </select>
  {/if}
</header>

<style>
  .sb {
    display: flex;
    align-items: center;
    gap: var(--s-2);
    padding: 4px var(--s-2);
    border-bottom: 1px solid var(--outline-variant);
    background: var(--surface-container-low);
    font-family: var(--font-mono);
    flex-shrink: 0;
    min-height: 36px;
  }

  .sb-hamburger {
    width: 32px;
    height: 28px;
    background: transparent;
    border: 1px solid var(--outline-variant);
    color: var(--accent);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color var(--t-fast), color var(--t-fast), background var(--t-fast);
  }
  .sb-hamburger:hover {
    border-color: var(--accent);
    background: var(--accent-faint);
  }
  .sb-hamburger-glyph {
    font-size: 16px;
    line-height: 1;
  }

  .sb-tag {
    color: var(--accent);
    font-weight: 700;
    font-size: 11px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  /* MEM / TASKS view switch. */
  .sb-viewtoggle {
    display: inline-flex;
    border: 1px solid var(--outline-variant);
    border-radius: 4px;
    overflow: hidden;
    flex-shrink: 0;
  }
  .sb-viewtoggle button {
    font-family: inherit;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    padding: 4px 9px;
    background: transparent;
    border: none;
    color: var(--on-surface-faint);
    cursor: pointer;
    transition: color var(--t-fast), background var(--t-fast);
  }
  .sb-viewtoggle button + button { border-left: 1px solid var(--outline-variant); }
  .sb-viewtoggle button:hover { color: var(--accent); }
  .sb-viewtoggle button.on { background: var(--accent-faint); color: var(--accent-bright); }

  .sb-status {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 8px;
    border: 1px solid var(--outline-variant);
    color: var(--on-surface-faint);
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .sb-status-dot {
    width: 6px; height: 6px;
    border-radius: 50%;
    background: #d65a5a;
    box-shadow: 0 0 4px rgba(214, 90, 90, 0.6);
    transition: background var(--t-fast), box-shadow var(--t-fast);
  }
  .sb-status.online .sb-status-dot {
    background: var(--accent);
    box-shadow: 0 0 6px var(--accent-soft);
  }
  .sb-status.online {
    color: var(--accent);
    border-color: var(--accent-soft);
  }
  .sb-status.retrying {
    color: var(--accent-bright);
    border-color: var(--accent);
  }
  .sb-status.retrying .sb-status-dot {
    background: var(--accent-bright);
    box-shadow: 0 0 8px var(--accent-bright);
    animation: sb-pulse 600ms ease-in-out infinite;
  }
  @keyframes sb-pulse {
    0%, 100% { opacity: 1.0 }
    50%      { opacity: 0.4 }
  }

  .sb-spacer { flex: 1; }

  .sb-sel, .sb-btn {
    font-family: inherit;
    font-size: 10px;
    color: var(--on-surface);
    background: var(--surface-container);
    border: 1px solid var(--outline-variant);
    padding: 4px 8px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    cursor: pointer;
    outline: none;
    transition: border-color var(--t-fast), color var(--t-fast), background var(--t-fast);
  }
  .sb-sel:hover, .sb-btn:hover { border-color: var(--accent); color: var(--accent); }
  .sb-sel:focus { border-color: var(--accent); }
  .sb-btn.on {
    color: var(--accent-bright);
    border-color: var(--accent);
    background: var(--accent-faint);
  }
  .sb-sel-narrow { max-width: 100px; }
</style>
